import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL;

interface Company {
  id: number;
  company_name: string;
}

interface ExpenseHead {
  id: number;
  name: string;
  company_id?: number | null;
}

interface StatementRow {
  id: number;
  date_str: string;
  particulars: string;
  debit: number;
  credit: number;
  mapped_invoice_id?: number | null;
  mapped_purchase_id?: number | null;
  mapped_loan_id?: number | null;
  mapped_expense_head_id?: number | null;
  mapped_fd_id?: number | null;
  company_id?: number | null;
}

interface Invoice {
  id: number;
  Company_id: number;
  Total_Amount: number;
  Date: string;
}

interface PurchaseInvoice {
  id: number;
  Company_id: number;
  Total_Amount: number;
  Date: string;
}

interface Loan {
  id: number;
  company_id: number;
  lender_name: string;
  total_amount: number;
}

interface FixedDeposit {
  id: number;
  company_id: number;
  fd_number: string;
  total_amount: number;
}

const ConsolidatedReport: React.FC = () => {
  const [companies, setCompanies] = useState<Company[]>([]);
  const [expenseHeads, setExpenseHeads] = useState<ExpenseHead[]>([]);
  const [rows, setRows] = useState<StatementRow[]>([]);
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [purchases, setPurchases] = useState<PurchaseInvoice[]>([]);
  const [loans, setLoans] = useState<Loan[]>([]);
  const [fds, setFds] = useState<FixedDeposit[]>([]);
  
  const [selectedCompanyId, setSelectedCompanyId] = useState<string>("");
  const [selectedYear, setSelectedYear] = useState<string>("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [compRes, headRes, rowsRes, invRes, purRes, loanRes, fdRes] = await Promise.all([
        axios.get(`${API_URL}/auth/companies`),
        axios.get(`${API_URL}/auth/expense-heads`),
        axios.get(`${API_URL}/auth/bank-statement-rows`),
        axios.get(`${API_URL}/auth/invoices`),
        axios.get(`${API_URL}/auth/purchase-invoices`),
        axios.get(`${API_URL}/auth/loans`),
        axios.get(`${API_URL}/auth/fixed-deposits`)
      ]);
      setCompanies(compRes.data || []);
      setExpenseHeads(headRes.data || []);
      setRows(rowsRes.data || []);
      setInvoices(invRes.data || []);
      setPurchases(purRes.data || []);
      setLoans(loanRes.data || []);
      setFds(fdRes.data || []);
    } catch (err) {
      console.error("Failed to load consolidated report data:", err);
    } finally {
      setLoading(false);
    }
  };

  const getYearFromDate = (dateStr: string): string => {
    if (!dateStr) return "";
    const parts = dateStr.split(/[-/\s.]+/);
    if (parts.length >= 3) {
      let yr = parts[2];
      if (parts[0].length === 4) yr = parts[0];
      return yr.length === 2 ? "20" + yr : yr;
    }
    return "";
  };

  const availableYears = useMemo(() => {
    const years = new Set<string>();
    rows.forEach(r => {
      const yr = getYearFromDate(r.date_str);
      if (yr) years.add(yr);
    });
    invoices.forEach(i => {
      const yr = getYearFromDate(i.Date);
      if (yr) years.add(yr);
    });
    return Array.from(years).sort((a, b) => b.localeCompare(a));
  }, [rows, invoices]);

  const reportData = useMemo(() => {
    const filteredInvoices = invoices.filter(inv => {
      const matchComp = !selectedCompanyId || String(inv.Company_id) === String(selectedCompanyId);
      const matchYear = !selectedYear || getYearFromDate(inv.Date) === selectedYear;
      return matchComp && matchYear;
    });

    const filteredPurchases = purchases.filter(pur => {
      const matchComp = !selectedCompanyId || String(pur.Company_id) === String(selectedCompanyId);
      const matchYear = !selectedYear || getYearFromDate(pur.Date) === selectedYear;
      return matchComp && matchYear;
    });

    const filteredRows = rows.filter(r => {
      const matchComp = !selectedCompanyId || String(r.company_id) === String(selectedCompanyId);
      const matchYear = !selectedYear || getYearFromDate(r.date_str) === selectedYear;
      return matchComp && matchYear;
    });

    const invoicedSales = filteredInvoices.reduce((sum, inv) => sum + Number(inv.Total_Amount || 0), 0);
    const directCredits = filteredRows
      .filter(r => r.credit > 0 && !r.mapped_invoice_id)
      .reduce((sum, r) => sum + Number(r.credit || 0), 0);

    const totalEarnings = invoicedSales + directCredits;

    const headsBreakdown = expenseHeads
      .filter(h => !selectedCompanyId || !h.company_id || String(h.company_id) === String(selectedCompanyId))
      .map(h => {
        const amount = filteredRows
          .filter(r => r.mapped_expense_head_id === h.id)
          .reduce((sum, r) => sum + Number(r.debit || 0), 0);
        return { id: h.id, name: h.name, amount };
      })
      .filter(h => h.amount > 0);

    const purchaseExpenses = filteredPurchases.reduce((sum, pur) => sum + Number(pur.Total_Amount || 0), 0);

    // Group loan payments dynamically
    const loanRepaymentsMap = new Map<number, { lenderName: string; amount: number }>();
    filteredRows.forEach(r => {
      if (r.mapped_loan_id) {
        const loanObj = loans.find(l => l.id === r.mapped_loan_id);
        const name = loanObj ? loanObj.lender_name : `Loan Mapped (#${r.mapped_loan_id})`;
        const amountVal = Number(r.debit || 0);
        if (amountVal > 0) {
          const current = loanRepaymentsMap.get(r.mapped_loan_id) || { lenderName: name, amount: 0 };
          current.amount += amountVal;
          loanRepaymentsMap.set(r.mapped_loan_id, current);
        }
      }
    });
    const loanRepaymentsList = Array.from(loanRepaymentsMap.values());
    const loanRepayments = loanRepaymentsList.reduce((sum, l) => sum + l.amount, 0);

    // Group FD investments dynamically
    const fdInvestmentsMap = new Map<number, { fdNumber: string; amount: number }>();
    filteredRows.forEach(r => {
      if (r.mapped_fd_id) {
        const fdObj = fds.find(f => f.id === r.mapped_fd_id);
        const fdNum = fdObj ? fdObj.fd_number : `FD Mapped (#${r.mapped_fd_id})`;
        const amountVal = Number(r.debit || 0);
        if (amountVal > 0) {
          const current = fdInvestmentsMap.get(r.mapped_fd_id) || { fdNumber: fdNum, amount: 0 };
          current.amount += amountVal;
          fdInvestmentsMap.set(r.mapped_fd_id, current);
        }
      }
    });
    const fdInvestmentsList = Array.from(fdInvestmentsMap.values());
    const fdInvestments = fdInvestmentsList.reduce((sum, f) => sum + f.amount, 0);

    const otherDirectDebits = filteredRows
      .filter(r => r.debit > 0 && !r.mapped_expense_head_id && !r.mapped_purchase_id && !r.mapped_loan_id && !r.mapped_fd_id)
      .reduce((sum, r) => sum + Number(r.debit || 0), 0);

    const totalExpenses = headsBreakdown.reduce((sum, h) => sum + h.amount, 0) + purchaseExpenses + loanRepayments + fdInvestments + otherDirectDebits;

    return {
      invoicedSales,
      directCredits,
      totalEarnings,
      headsBreakdown,
      purchaseExpenses,
      loanRepayments,
      loanRepaymentsList,
      fdInvestments,
      fdInvestmentsList,
      otherDirectDebits,
      totalExpenses
    };
  }, [selectedCompanyId, selectedYear, companies, expenseHeads, rows, invoices, purchases, loans, fds]);

  return (
    <div className="bg-white shadow-xl border border-blue-100 rounded-3xl p-6">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
        <div>
          <h3 className="font-extrabold text-blue-900 text-xl">Consolidated Expenses & Earnings Report</h3>
          <p className="text-gray-500 text-xs">Summary of earnings against categorized expenses.</p>
        </div>

        <div className="flex flex-wrap gap-3">
          <select
            value={selectedCompanyId}
            onChange={e => setSelectedCompanyId(e.target.value)}
            className="form-select border border-gray-300 rounded-xl px-3 py-2 text-xs font-semibold bg-white text-slate-800 focus:outline-none"
          >
            <option value="">All Companies</option>
            {companies.map(c => (
              <option key={c.id} value={c.id}>{c.company_name}</option>
            ))}
          </select>

          <select
            value={selectedYear}
            onChange={e => setSelectedYear(e.target.value)}
            className="form-select border border-gray-300 rounded-xl px-3 py-2 text-xs font-semibold bg-white text-slate-800 focus:outline-none"
          >
            <option value="">All Years</option>
            {availableYears.map(yr => (
              <option key={yr} value={yr}>{yr}</option>
            ))}
          </select>
        </div>
      </div>

      {loading ? (
        <div className="py-20 text-center text-gray-500 font-medium text-sm">Loading consolidated figures...</div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          <div className="bg-emerald-50/30 border border-emerald-100 rounded-3xl p-6">
            <h4 className="font-bold text-emerald-900 text-sm mb-4 uppercase tracking-wider">🟢 Total Earnings</h4>
            
            <div className="space-y-4 mb-6">
              <div className="flex justify-between items-center pb-2 border-b border-emerald-100">
                <span className="text-sm font-semibold text-emerald-950">Invoiced Sales Revenue</span>
                <span className="text-sm font-extrabold text-slate-900">₹{reportData.invoicedSales.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</span>
              </div>
              <div className="flex justify-between items-center pb-2 border-b border-emerald-100">
                <span className="text-sm font-semibold text-emerald-950">Direct Statement Credit (Other Incomes)</span>
                <span className="text-sm font-extrabold text-slate-900">₹{reportData.directCredits.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</span>
              </div>
            </div>

            <div className="bg-emerald-600 rounded-2xl p-4 flex justify-between items-center text-white">
              <span className="text-sm font-bold uppercase">Net Consolidated Earnings</span>
              <span className="text-lg font-black">₹{reportData.totalEarnings.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</span>
            </div>
          </div>

          <div className="bg-rose-50/30 border border-rose-100 rounded-3xl p-6">
            <h4 className="font-bold text-rose-900 text-sm mb-4 uppercase tracking-wider">🔴 Categorized Expenses & Outflows</h4>
            
            <div className="space-y-4 mb-6 max-h-[350px] overflow-y-auto pr-2">
              {reportData.purchaseExpenses > 0 && (
                <div className="flex justify-between items-center pb-2 border-b border-rose-100">
                  <span className="text-sm font-semibold text-rose-950">Bills / Purchase Invoices</span>
                  <span className="text-sm font-extrabold text-slate-900">₹{reportData.purchaseExpenses.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</span>
                </div>
              )}
              
              {reportData.headsBreakdown.map(h => (
                <div key={h.id} className="flex justify-between items-center pb-2 border-b border-rose-100">
                  <span className="text-sm font-semibold text-rose-950">Head: {h.name}</span>
                  <span className="text-sm font-extrabold text-slate-900">₹{h.amount.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</span>
                </div>
              ))}

              {reportData.loanRepayments > 0 && (
                <div>
                  <div className="flex justify-between items-center pb-1 border-b border-rose-100">
                    <span className="text-sm font-semibold text-rose-950">Loan EMI / Repayments</span>
                    <span className="text-sm font-extrabold text-slate-900">₹{reportData.loanRepayments.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</span>
                  </div>
                  <div className="pl-4 py-1 space-y-1">
                    {reportData.loanRepaymentsList.map((l, idx) => (
                      <div key={idx} className="flex justify-between text-xs text-rose-800/80 font-medium">
                        <span>Lender: {l.lenderName}</span>
                        <span>₹{l.amount.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {reportData.fdInvestments > 0 && (
                <div>
                  <div className="flex justify-between items-center pb-1 border-b border-rose-100">
                    <span className="text-sm font-semibold text-rose-950">FD Investments (Outflows)</span>
                    <span className="text-sm font-extrabold text-slate-900">₹{reportData.fdInvestments.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</span>
                  </div>
                  <div className="pl-4 py-1 space-y-1">
                    {reportData.fdInvestmentsList.map((f, idx) => (
                      <div key={idx} className="flex justify-between text-xs text-rose-800/80 font-medium">
                        <span>FD Number: {f.fdNumber}</span>
                        <span>₹{f.amount.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {reportData.otherDirectDebits > 0 && (
                <div className="flex justify-between items-center pb-2 border-b border-rose-100">
                  <span className="text-sm font-semibold text-rose-950">Direct Bank Debits (Other Expenses)</span>
                  <span className="text-sm font-extrabold text-slate-900">₹{reportData.otherDirectDebits.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</span>
                </div>
              )}
            </div>

            <div className="bg-rose-600 rounded-2xl p-4 flex justify-between items-center text-white">
              <span className="text-sm font-bold uppercase">Net Consolidated Outflow</span>
              <span className="text-lg font-black">₹{reportData.totalExpenses.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ConsolidatedReport;
